import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Crown, Clock, CreditCard } from 'lucide-react';
import type { FormData } from '../types';

interface EnterpriseControlsFieldSimpleProps {
  register: UseFormRegister<FormData>;
  errors: FieldErrors<FormData>;
  setValue: UseFormSetValue<FormData>;
  watch: UseFormWatch<FormData>;
}

export const EnterpriseControlsFieldSimple = ({ 
  register, 
  errors, 
  setValue, 
  watch 
}: EnterpriseControlsFieldSimpleProps) => {
  console.log('EnterpriseControlsFieldSimple: Starting render');
  
  const enterpriseFeaturesEnabled = watch('enterprise_features_enabled');
  const subscriptionTier = watch('subscription_tier');
  const subscriptionStatus = watch('subscription_status');
  
  console.log('EnterpriseControlsFieldSimple: Values loaded', {
    enterpriseFeaturesEnabled,
    subscriptionTier,
    subscriptionStatus
  });

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Crown className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Enterprise Controls</CardTitle>
          <Badge variant="outline" className="text-primary border-primary/30">
            Admin Only
          </Badge>
        </div>
        <CardDescription>
          Manage enterprise features and subscription settings for this PEO provider
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Enterprise Features Toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <label htmlFor="enterprise-features" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Enable Enterprise Features</label>
            <p className="text-sm text-muted-foreground">
              Override subscription to grant enterprise access
            </p>
          </div>
          <Switch
            id="enterprise-features"
            checked={enterpriseFeaturesEnabled}
            onCheckedChange={(checked) => setValue('enterprise_features_enabled', checked)}
          />
        </div>

        {/* Subscription Tier */}
        <div className="space-y-2">
          <label htmlFor="subscription-tier" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Subscription Tier</label>
          <Select
            value={subscriptionTier}
            onValueChange={(value) => setValue('subscription_tier', value as 'basic' | 'enterprise')}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select tier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="basic">
                <div className="flex items-center space-x-2">
                  <span>Basic</span>
                  <Badge variant="secondary">Free</Badge>
                </div>
              </SelectItem>
              <SelectItem value="enterprise">
                <div className="flex items-center space-x-2">
                  <Crown className="h-4 w-4" />
                  <span>Enterprise</span>
                  <Badge className="bg-primary/10 text-primary">$2,000/mo</Badge>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Subscription Status */}
        <div className="space-y-2">
          <label htmlFor="subscription-status" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Subscription Status</label>
          <Select
            value={subscriptionStatus || 'active'}
            onValueChange={(value) => setValue('subscription_status', value as any)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">
                <div className="flex items-center space-x-2">
                  <div className="h-2 w-2 bg-primary rounded-full" />
                  <span>Active</span>
                </div>
              </SelectItem>
              <SelectItem value="trialing">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span>Trialing (2 months free)</span>
                </div>
              </SelectItem>
              <SelectItem value="past_due">
                <div className="flex items-center space-x-2">
                  <CreditCard className="h-4 w-4 text-destructive" />
                  <span>Past Due</span>
                </div>
              </SelectItem>
              <SelectItem value="cancelled">
                <div className="flex items-center space-x-2">
                  <div className="h-2 w-2 bg-muted-foreground rounded-full" />
                  <span>Cancelled</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Referral Code (simplified without generation) */}
        <div className="space-y-2">
          <label htmlFor="referral-code" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Referral Code</label>
          <Input
            {...register('referral_code')}
            placeholder="Enter referral code manually"
          />
          <p className="text-xs text-muted-foreground">
            Referral code generation temporarily disabled for debugging.
          </p>
          {errors.referral_code && (
            <p className="text-sm text-destructive">{errors.referral_code.message}</p>
          )}
        </div>

        {/* Feature Summary */}
        {enterpriseFeaturesEnabled && (
          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
            <h4 className="font-medium text-primary mb-2">Enabled Enterprise Features:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Shopper linking and management</li>
              <li>• Advanced analytics dashboard</li>
              <li>• Custom application workflows</li>
              <li>• White-label branding options</li>
              <li>• Priority support access</li>
            </ul>
            <div className="mt-3 p-3 bg-primary/10 rounded border border-primary/30">
              <p className="text-sm font-medium text-primary">
                Pricing: 2-month free trial, then $2,000/month
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};